import { useAction } from '@reatom/npm-react'
import styled, { css } from 'styled-components'
import { useMemo } from 'use-memo-one'

import { ArrowRight2Icon, Button } from '@blade/ui-kit'
import { redirectToGoogleAction } from '@root/features/auth'
import { reatomTelegramAuthentication } from '@root/features/auth/telegram'
import { redirectToTwitterAction } from '@root/features/auth/twitter/atoms'
import { loginWithUsernameClickedAction } from '@root/features/initialization/atoms/common'
import { policyScreenAction } from '@root/features/settings/settings-screen'
import { useTranslation } from '@root/shared/localization'
import { AuthButtonSocial, AuthModal } from '@root/shared/ui/auth-modal'
import {
  GoogleIcon,
  mediaMaxMobile,
  ProfileCircleIcon,
  ProfileIcon,
  TelegramIcon,
  XSocialIcon,
} from '@root/shared/ui-kit'

import type { SettingScreenPolicy } from '@root/features/settings/settings-screen'

export const AuthInitial = () => {
  const { t } = useTranslation()
  const loginWithUsernameClicked = useAction(loginWithUsernameClickedAction)
  const policyScreen = useAction(policyScreenAction)

  const telegramAuthAtom = useMemo(
    // eslint-disable-next-line @reatom/unit-naming-rule
    () => reatomTelegramAuthentication(),
    [],
  )

  const redirectToGoogle = useAction(redirectToGoogleAction)
  const redirectToTwitter = useAction(redirectToTwitterAction)
  const startTelegramLogin = useAction(telegramAuthAtom.execute)
  const handlePolicyClick = (policy: SettingScreenPolicy) =>
    policyScreen(policy)

  return (
    <AuthModal infoIcon={ProfileIcon} infoMessage={t('auth.info.wallets')}>
      <Root>
        <Heading>
          <Title>{t('auth.welcome')}</Title>
          <Intro>{t('auth.log-in-to-your-account')}</Intro>
        </Heading>
        <ButtonUsername
          fullWidth
          onClick={() => loginWithUsernameClicked()}
          iconRight={<ArrowRight2Icon />}
          size="m"
        >
          <ProfileCircleIcon size={32} />
          {t('auth.login-with-username')}
        </ButtonUsername>
        <Note>
          <SeparatorLeft />
          {t('auth.or-login-or-register')}
          <SeparatorRight />
        </Note>
        <AuthButtons>
          <AuthButtonSocial onClick={startTelegramLogin}>
            <TelegramIcon size={24} />
            Telegram
          </AuthButtonSocial>
          <AuthButtonSocial onClick={() => redirectToGoogle()}>
            <GoogleIcon size={20} />
            Google
          </AuthButtonSocial>
          <AuthButtonSocial onClick={() => redirectToTwitter()}>
            <XSocialIcon size={20} />X
          </AuthButtonSocial>
        </AuthButtons>
        <AuthInitialTerms>
          <span key={0} onClick={() => handlePolicyClick('terms-of-service')}>
            {t('policies.terms-of-service')}
          </span>
          <Separator />
          <span key={1} onClick={() => handlePolicyClick('privacy-policy')}>
            {t('policies.privacy-policy')}
          </span>
        </AuthInitialTerms>
      </Root>
    </AuthModal>
  )
}

const Root = styled.div`
  display: grid;
  gap: 18px;
`

const AuthButtons = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
`

const Heading = styled.div`
  display: grid;
`

const Title = styled.h2`
  color: var(--text-12);
  text-align: center;
  font-style: normal;

  font-size: 18px;
  font-weight: 700;
  line-height: 20px;

  ${mediaMaxMobile(css`
    /* Mobile/14 SemiBold */
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
  `)}
`

const Intro = styled.h3`
  color: var(--text-11);
  text-align: center;
  font-style: normal;

  font-size: 14px;
  font-weight: 500;
  line-height: 20px;

  ${mediaMaxMobile(css`
    /* Mobile/12 Medium */
    font-size: 12px;
    font-weight: 500;
    line-height: 16px;
  `)}
`

const ButtonUsername = styled(Button)`
  border: 1px solid var(--backgroundA-5);
  background:
    radial-gradient(
      610% 93.85% at -43.85% 50%,
      transparent 100%,
      var(--backgroundA-4) 0%
    ),
    var(--backgroundA-4);

  color: var(--appearance-inv-1);
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;

  gap: 10px;
  padding: 0 12px;

  justify-content: space-between;
  align-items: center;
  text-align: start;
  display: grid;
  grid-template-columns: auto 1fr auto;

  --button-radius-full: 12px;

  svg:last-of-type {
    width: 20px;
    height: 20px;
    color: var(--text-10);
  }

  &:hover {
    background: var(--backgroundA-5);
  }
`

const Note = styled.div`
  color: var(--text-11);
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
  text-align: center;

  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 10px;

  justify-content: center;
  align-items: center;
`

const SeparatorLeft = styled.span`
  opacity: 0.5;
  height: 1px;
  background: linear-gradient(
    270deg,
    rgba(255 255 255 / 15%) 50%,
    transparent 100%
  );
`

const SeparatorRight = styled.span`
  opacity: 0.5;
  height: 1px;
  background: linear-gradient(
    90deg,
    rgba(255 255 255 / 15%) 50%,
    transparent 100%
  );
`

const AuthInitialTerms = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: auto 1px auto;

  justify-content: center;
  justify-items: center;
  align-items: center;

  span {
    cursor: pointer;
    color: var(--textA-9);
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px;

    transition: color 0.15s ease-in-out;

    &:hover {
      color: var(--text-12);
    }
  }
`

const Separator = styled.span`
  width: 1px;
  height: 10px;
  background: var(--backgroundA-3);
`
